import React, { FC, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Product, Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { StringHelpers } from 'helpers/stringHelpers';
import { desktop, mobile } from 'helpers/utils/screensizes';
import { useAccount, useCart, useProduct } from 'frontastic';
import { ImageProps } from 'frontastic/lib/image';
import AdditionalInfo from './components/additional-info';
import ProductInformation from './components/product-information';
import ShippingSection from './components/shipping-section';
import useTrack from './hooks/useTrack';
import { CartDiscount, UIProduct } from './types';
import WishlistButton from '../../wishlist/components/wishlist-button';
import useCustomFit from 'frontastic/hooks/useCustomFit';
import Loading from 'components/commercetools-ui/atoms/loading';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import Modal from 'components/commercetools-ui/atoms/modal';
import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import PayPalMessages from './components/paypal-message';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import ChevronRight from 'public/icons/ag-icons/ChevronRight.svg';
import { Price } from 'shared/types/product/Variant';
import { useCookies } from 'react-cookie';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { calculateDeliveryDate } from './components/customfit-new';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import PopUp from './components/popUp';
import { ButtonBlock, CustomFitPopUpData } from 'shared/types/product/Product';
import { MAIN_CATEGORIES } from 'helpers/constants/categories';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { CLICKED_OBJECT_ID, IS_SEARCH, OBJECT_ID_QUERY_ID, SEARCH_QUERY_ID } from 'helpers/constants/algolia';
import { P2Badge } from '../product-list-algolia/types';
import { useBadgeLoader } from 'helpers/utils/badgeLoader';
import sale from 'public/locales/en/product.json';
import ResponsiveV12Modal from './components/v12-popup-modal';
import { fetchTrustPilotData } from 'helpers/utils/trustPilotReview';
import { PersonalisationBalls, PersonalisationPayload } from 'types/datasource';

export interface ProductDetailsProps {
  pdpProduct: UIProduct;
  product: UIProduct;
  productLogo?: string;
  variant: Variant | undefined;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  handleScroll?: () => void;
  setIsCartDisable?: (isCartDisable: boolean) => void;
  setHoveredImage?: (img: string | null | undefined) => void;
  handleScrollReviews?: any;
  setCustomFitPrice: (customFitPrice: number | null) => void;
  customFitPrice: any;
  setQuantity?: any;
  quantity?: number;
  setPayloadAttributes?: any;
  setSelectedSKUs?: React.Dispatch<React.SetStateAction<{ Shaft?: string; Grip?: string }>>;
  setDeliveryDate?: any;
  deliveryDate?: any;
  payloadAttributes?: any;
  isCartDisable?: any;
  selectedSKUs?: any;
  breadCrumb?: BreadCrumb[];
  setColorVariant?: (variant: Variant | undefined) => void;
  handleColorVariant?: (sku: string) => void;
  viewStandardFitOptions?: ButtonBlock[];
  trustBoxCss?: TrustBoxCss;
  allBadges?: any;
  cartDiscountBadge?: any;
  isDesktopSize?: boolean;
  isMobile?: boolean;
  selectedLocale?: string;
  StarImageComponent?: any;
  starsAverage?: any;
  numberOfReviews?: any;
  setDiscountedMRPrices?: any;
  discountedMRPrices?: any;
  colorVariant?: Variant | undefined;
  isPersonalized?: boolean | undefined;
  personalisationBalls?: PersonalisationBalls;
  minOrderQuantity?: number;
  onPersonalisationChange?: (data: {
    lines: { id: number; text: string }[];
    inkColour: any;
    inkColourHexCode: any;
  }) => void;
  personalisationData?: {
    lines: { id: number; text: string }[];
    inkColour: string;
    inkColourHexCode: string;
  };
}
export interface ProductInformationProps {
  product: UIProduct; // Ensure the 'product' property is included
  variant: Variant | undefined;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  handleScroll: () => void;
  setIsCartDisable: React.Dispatch<React.SetStateAction<boolean>>;
  setHoveredImage: (img: string | null | undefined) => void;
  handleScrollReviews: any;
  setCustomFitPrice: (customFitPrice: number | null) => void;
  customFitPrice: any;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  quantity: number;
  setPayloadAttributes: React.Dispatch<React.SetStateAction<{ name: string; value: string | string[] }[]>>;
  setDeliveryDate?: any;
  deliveryDate?: any;
  setSelectedSKUs: React.Dispatch<React.SetStateAction<{ Shaft?: string; Grip?: string }>>;
  payloadAttributes: { name: string; value: string | string[] }[];
  isCartDisable: boolean;
  selectedSKUs: { Shaft?: string; Grip?: string };
  breadCrumb: BreadCrumb[];
  setColorVariant?: (variant: Variant | undefined) => void;
  trustBoxCss: TrustBoxCss;
  personalisationBalls?: PersonalisationBalls;
}
const ProductDetails: FC<ProductDetailsProps> = ({
  pdpProduct,
  productLogo,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  setCustomFitPrice,
  customFitPrice,
  trustBoxCss,
  setColorVariant,
  colorVariant,
  personalisationBalls,
}) => {
  useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const Selectedvariant: any = searchParams.get('variant');
  const { addItem } = useCart();
  // const { formatMessage } = useFormat({ name: 'cart' });
  const { ctLocale: locale, locale: siteLocale } = useLocale();
  const [product, setProducts] = useState(null as unknown as UIProduct);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const [paypalModal, setPaypayModal] = useState(false);
  const [v12Modal, setV12Modal] = useState(false);

  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMobile] = useMediaQuery(mobile);
  const [category, setCategory] = useState<Category>();
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const { logout, account: isLoggedIn, loggedIn, result } = useAccount();
  const router = useRouter();
  const [isCartDisable, setIsCartDisable] = useState<boolean>(false);
  const [hoveredImage, setHoveredImage] = useState<string | null | undefined>(null);
  const [cartError, setCartError] = useState(false);
  // const [customFitPrice, setCustomFitPrice] = useState<number | null>(null);
  const { categories, getProduct } = useProduct();
  const scrollToRefReview = useRef<HTMLDivElement | null>(null);
  const paypal = product?.paymentPromotion?.paypal;
  const v12 = product?.paymentPromotion?.v12;
  const paymentOverlayContentRef = useRef<HTMLIFrameElement | null>(null);
  const messageContainer = useRef<HTMLDivElement>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const [payloadAttributes, setPayloadAttributes] = useState<{ name: string; value: string | string[] }[]>([]);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [selectedSKUs, setSelectedSKUs] = useState<{ Shaft?: string; Grip?: string }>({});
  const [isProduct, setProduct] = useState(false);
  const [addTocart, setAddTocart] = useState(false);
  const [isbuttonDisable, setIsbuttonDisable] = useState<any>(false);
  const [addedVariant, setAddedVariant] = useState<string | null>(null);
  const [personalisationData, setPersonalisationData] = useState<{
    lines: { id: number; text: string }[];
    inkColour: string;
    inkColourHexCode: string;
  }>({
    lines: [],
    inkColour: '',
    inkColourHexCode: '',
  });

  const [isTimeout, setIsTimeout] = useState(true);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [StarImage, setStarImage] = useState<React.FC | null>(null);
  const [starsAverage, setStarsAverage] = useState<number | null>(null);
  const [numberOfReviews, setNumberOfReviews] = useState<number | null>(null);
  const [discountedMRPrices, setDiscountedMRPrices] = useState<any>(null);

  const [breadCrumb, setBreadCrumb] = useState<BreadCrumb[]>([]);
  const popUpData = product?.popupData as CustomFitPopUpData;
  const [ismobile] = useMediaQuery(mobile);
  const objectID = sessionStorage?.getItem(CLICKED_OBJECT_ID);
  const searchQueryId = sessionStorage?.getItem(SEARCH_QUERY_ID);
  const searchPage = sessionStorage?.getItem(IS_SEARCH);
  const isSearchItem = searchPage === 'true';
  const userId = loggedIn
    ? result?.data?.data?.account?.accountId
    : typeof window !== 'undefined' && localStorage?.getItem('__anon_id');
  const analytics = new AlgoliaAnalytics(siteLocale);
  const initialViewTracked = useRef(false);
  const customFitVariant = variant?.attributes?.isCustomFit ?? product?.variants?.[0]?.attributes?.isCustomFit;
  // Need to fix here
  const isPersonalized = product?.variants?.[0]?.attributes?.itemSubGroupcode === 'PERSONALIS';
  //  const isPersonalized = true;
  const minOrderQuantity = isPersonalized
    ? (product?.variants?.[0]?.attributes && product?.variants?.[0]?.attributes?.personalisedMinQuantity) || 2
    : 1;
  const now = new Date();
  const selectedLocale = cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU';
  const p1BadgeData = useBadgeLoader(product?.p1BadgeData?.productListingContent);
  const p2BadgeData = product?.p2BadgeData?.productListingContent as P2Badge[] | undefined;
  const priceData = useMemo(() => {
    try {
      const standalonePrice = variant?.attributes?.standalonePrice
        ? JSON.parse(variant?.attributes?.standalonePrice)
        : (product as Product)?.variants?.[0]?.attributes?.standalonePrice
        ? JSON.parse((product as Product)?.variants?.[0]?.attributes?.standalonePrice)
        : {};
      const currencyKey = cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU';
      const selectedPrice = standalonePrice?.[currencyKey]?.value;
      const discountedPrices = standalonePrice?.[currencyKey]?.custom || null;
      let mrrp = discountedPrices?.mrrp?.centAmount || 0;
      let variantPrice = selectedPrice?.centAmount || 0;
      let discountPercentage = null;

      // Custom Fit Discount Calculation
      if (product?.customFitDetails && customFitVariant) {
        // Use discountedMRPrices and customFitPrice for calculation
        if (discountedMRPrices && customFitPrice) {
          mrrp = discountedMRPrices || 0;
          variantPrice = customFitPrice || 0;
          if (
            discountedMRPrices <= 0 ||
            customFitPrice <= 0 ||
            discountedMRPrices === customFitPrice ||
            discountedMRPrices < customFitPrice
          ) {
            discountPercentage = 0;
          } else {
            discountPercentage = Math.round(((discountedMRPrices - customFitPrice) / discountedMRPrices) * 100);
          }
        }
      } else {
        // Normal calculation
        if (discountedPrices && discountedPrices?.mrrp?.centAmount && variantPrice) {
          if (mrrp <= 0 || variantPrice <= 0 || mrrp === variantPrice || mrrp < variantPrice) {
            discountPercentage = 0;
          } else {
            discountPercentage = Math.round(((mrrp - variantPrice) / mrrp) * 100);
          }
        }
      }

      return { mrrp, variantPrice, discountPercentage };
    } catch (error) {
      console.error('Error parsing standalonePrice:', error);
      return {};
    }
  }, [variant, customFitVariant, customFitPrice, discountedMRPrices, cookies?.locale_selection?.currency]);

  const mrrp = priceData?.mrrp ?? 0;
  const variantPrice = priceData?.variantPrice ?? 0;
  const discountPercentage = priceData?.discountPercentage ?? 0;
  //Old Implementaion
  // const sale = useMemo(() => {
  //   if (!product?.categories) return false;

  //   return product.categories.some((category: Category) => {
  //     const name = category?.name?.toLowerCase?.();
  //     const slug = category?.slug?.toLowerCase?.();
  //     return (typeof name === 'string' && name.includes('sale')) || (typeof slug === 'string' && slug.includes('sale'));
  //   });
  // }, [product]);
  const isSale = useMemo(() => {
    const includeInSale = variant?.attributes?.includeInSale;
    if (includeInSale === true) return true;
    if (includeInSale === false) return false;
    const seasonCode = variant?.attributes?.seasonCode || '';
    const isValidSeasonCode =
      seasonCode &&
      !isNaN(Number(seasonCode)) &&
      product?.currentSeasonCode &&
      discountPercentage !== null &&
      Number(seasonCode) < Number(product?.currentSeasonCode) &&
      seasonCode !== '1111' &&
      seasonCode !== '9999';
    return isValidSeasonCode && discountPercentage >= 10;
  }, [product, variant, discountPercentage]);
  // const isNew = useMemo(() => {
  //   const dateStr = variant?.attributes?.firstReceiptDate;
  //   if (!dateStr) return false;
  //   const firstReceipt = new Date(dateStr as string);
  //   if (isNaN(firstReceipt?.getTime())) return false;
  //   const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  //   return firstReceipt >= twoMonthsAgo && firstReceipt <= now;
  // }, [product, variant]);
  const isNew = useMemo(() => {
    if (!product?.categories) return false;

    return product.categories.some((category: Category) => {
      const name = category?.name?.toLowerCase?.();
      const slug = category?.slug?.toLowerCase?.();
      return (
        (typeof name === 'string' && name?.includes('new arrivals')) ||
        (typeof slug === 'string' && slug.includes('new'))
      );
    });
    //     const categories = (product?.categories as any)?.['en-GB']?.lvl0;
    // if (!Array.isArray(categories)) return false;
    // return categories?.some((category: string) => category?.toLowerCase?.().includes('new arrivals'));
  }, [product?.categories]);
  const isBadgeValid = (badge: any, now: Date) => {
    const { isHidden, validFrom, validTo } = badge.conditions || {};
    if (isHidden) return false;
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    return (!from || from <= now) && (!to || to >= now);
  };
  const activeP1Badges = useMemo(
    () =>
      p1BadgeData?.filter(
        (badgemap: any) =>
          badgemap?.variantCodes?.includes(variant?.attributes?.variantCode) && isBadgeValid(badgemap, now),
      ) || [],
    [p1BadgeData, variant, now],
  );
  const cartDiscountBadge = p1BadgeData?.find((badge: any) => badge?.label?.[selectedLocale] === 'cart discount');

  const activeP2Badges = [
    isNew ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'new' && isBadgeValid(badge, now)) : null,
    isSale ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'sale' && isBadgeValid(badge, now)) : null,
    customFitVariant
      ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'customFit' && isBadgeValid(badge, now))
      : null,
  ].filter(Boolean);

  const getRanking = (badge: any) => Number(badge?.ranking ?? 99);
  const sortedP1Badges = [...(activeP1Badges || [])].sort((a, b) => getRanking(a) - getRanking(b));
  const sortedP2Badges = [...(activeP2Badges || [])].sort((a, b) => getRanking(a) - getRanking(b));
  const allBadges = [...sortedP1Badges, ...sortedP2Badges];

  const handleColorVariant = (sku: string) => {
    const variantsToUse = product?.variants?.find((variant) => variant?.sku === sku);
    if (variantsToUse) {
      setColorVariant?.(variantsToUse);
    }
  };

  useEffect(() => {
    if (pdpProduct) {
      setProducts(pdpProduct as any);
    }
  }, [pdpProduct]);

  useEffect(() => {
    if (product?.breadCrumbList?.length > 0) {
      const breadCrumbList = product.breadCrumbList;
      const validBreadcrumbs = breadCrumbList.filter((b: any) => b?.hierarchy?.length > 0);

      if (validBreadcrumbs.length === 0) {
        setBreadCrumb(product?.breadCrumb);
        return;
      }

      // Sort by our priority rules
      const sortedBreadcrumbs = [...validBreadcrumbs].sort((a, b) => {
        const getPriority = (item: any) => {
          const hierarchy = item?.hierarchy ?? [];
          const firstName = hierarchy?.[0]?.name?.toLowerCase();

          const isMain = MAIN_CATEGORIES?.some((cat) => cat?.toLowerCase() === firstName);
          const isBrand = hierarchy?.[0]?.name?.toUpperCase() === 'BRANDS';

          if (isMain && hierarchy?.length >= 2) return 1; // Main → Sub
          if (isMain && hierarchy?.length === 1) return 2; // Main only
          if (isBrand) return 3; // Brand
          return 4; // Lowest priority
        };

        return getPriority(a) - getPriority(b);
      });

      const selectedBreadcrumb = sortedBreadcrumbs?.[0];

      const hierarchyToUse = selectedBreadcrumb?.hierarchy ?? [];

      const cleanedHierarchy = hierarchyToUse
        ?.filter((item: any) => item?.name?.toUpperCase() !== 'BRANDS')
        ?.map((item: any) => ({
          ...item,
          href: item?.href?.replace(/^\/brands/, ''),
        }));

      setBreadCrumb([{ href: '/', name: 'Home' }, ...cleanedHierarchy]);
    } else if (product?.breadCrumb) {
      setBreadCrumb(product?.breadCrumb);
    }
  }, [product, pathname]);

  // const [selectedAttributesState, setSelectedAttributesState] = useState<{ name: string; value: string | string[] }[]>(
  //   [],
  // );
  // Threshold values
  const paypalThresholdMin = 100;
  const paypalThresholdMax = 249;
  const v12ThresholdMin = v12?.v12ThresholdMin ?? 250;
  const v12ThresholdMax = v12?.v12ThresholdMax ?? 499.99;
  const v12HighThreshold = v12?.v12HighThreshold ?? 500;

  const handlePaypalPromotion = () => {
    setPaypayModal(true);
  };
  const handleV12Promotion = () => {
    setV12Modal(true);
  };

  const customBookingLink = process.env.NEXT_PUBLIC_CUSTOM_BOOKING_URL;

  const price = useMemo(() => {
    // Parse standalonePrice JSON from attributes
    const standalonePrice = variant?.attributes?.standalonePrice
      ? JSON.parse(variant.attributes.standalonePrice)
      : (product as any)?.variants[0]?.attributes.standalonePrce
      ? JSON.parse((product as any)?.variants[0].attributes.standalonePrice)
      : null;

    return standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;
  }, [variant, cookies?.locale_selection?.currency]);

  const productPrice = price ? (price?.centAmount as number) / Math.pow(10, price?.fractionDigits as number) : 0;
  const discountPrice = isNaN(
    parseFloat(
      CurrencyHelpers.formatForCurrency(
        ((variant as any)?.discountedPrice ?? product?.variants?.[0].discountedPrice) as number,
        locale,
      ).replace(/[^\d.-]/g, ''),
    ),
  )
    ? 0
    : parseFloat(
        CurrencyHelpers.formatForCurrency(
          ((variant as any)?.discountedPrice ?? product?.variants?.[0].discountedPrice) as number,
          locale,
        ).replace(/[^\d.-]/g, ''),
      );
  const finalPrice: any = customFitPrice || discountPrice || productPrice;
  const paypalDivideAmount = finalPrice / paypal?.splitInfoV1;
  const v12DivideAmountV1 = (finalPrice / v12?.splitInfoV1)?.toFixed(2);
  const v12DivideAmountV2 = (finalPrice / v12?.splitInfoV2)?.toFixed(2);

  const handleScrollReviews = useCallback(() => {
    if (scrollToRefReview?.current) {
      setReviewOpen(true);
      const yOffset = -200;
      const y = scrollToRefReview?.current?.getBoundingClientRect()?.top + window?.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [scrollToRefReview, reviewOpen]);
  useEffect(() => {
    if (product && categories && !category) {
      const currentCategory = categories.find((cat) => cat.categoryId === product.categories?.[0]?.categoryId);
      setCategory(currentCategory);
    }
  }, [categories, category, product, product?.categories]);

  const cartDiscount =
    variant?.attributes?.cartDiscount && (JSON.parse(variant?.attributes?.cartDiscount as string) as CartDiscount);

  const parentCategoryLink = product?.breadCrumb?.[1]?.href.replace(/\/$/, '');

  const { trackAddToCart } = useTrack({ product, inModalVersion });

  const { show, fetchRelatedProducts, setProductLogo } = useAddToCartOverlay();

  const handleQuantityChange = (e: React.FormEvent) => {
    setQuantity(+(e.target as HTMLSelectElement).value);
  };

  const customFitPayLoadMapper = (sku: any, payloadAttributes: any) => {
    const payload = {} as any;
    if (payloadAttributes?.length > 0 && sku) {
      payloadAttributes.forEach((item: any) => {
        const attributeSku =
          (item.name === 'Shaft' && selectedSKUs?.Shaft) || (item.name === 'Grip' && selectedSKUs?.Grip)
            ? selectedSKUs?.[item.name as keyof typeof selectedSKUs]
            : sku;
        const key = `dwopt_${attributeSku}_${item.name.toLowerCase()}`;
        payload[key] = `${item.name}: ${item.value}`;
      });

      // Add ETA field after the loop
      const etaKey = `dwopt_${sku}_eta`;
      const etaDays = product?.customFitDetails?.[0]?.productJSON?.ETADays || 5; // Default to 5 if ETADays is not available
      const etaValue = calculateDeliveryDate(etaDays);
      payload[etaKey] = `Estimated dispatch date: ${deliveryDate}`;
    }
    return payload;
  };

  const personalisationPayLoadMapper = () => {
    const personalisationPayload: PersonalisationPayload = {
      inkColour: personalisationData?.inkColour,
      inkColourHexCode: personalisationData?.inkColourHexCode,
    };

    personalisationData?.lines?.forEach((line, index) => {
      if (line?.text?.trim()?.length >= 2) {
        personalisationPayload[`personalisation_text${index + 1}`] = line?.text;
      }
    });

    return personalisationPayload;
  };

  useEffect(() => {
    setAddedVariant(null);
  }, [variant]);

  const handleAddToCart = async () => {
    setLoading(true);
    let payload = isPersonalized
      ? personalisationPayLoadMapper()
      : customFitPayLoadMapper(variant?.sku ?? product?.variants?.[0]?.sku, payloadAttributes);
    const isIronsCategory = payloadAttributes?.some((item) => item?.name?.toLowerCase()?.includes('irons'));
    const ironCustomPrice = isIronsCategory ? customFitPrice / quantity : customFitPrice;
    payload = {
      ...payload,
      customPrice: ironCustomPrice,
      pid: variant?.sku ?? product?.variants?.[0]?.sku,
      sku: variant?.sku ?? product?.variants?.[0]?.sku,
      queryID: searchQueryId || '',
    };
    addItem(payload, quantity).then((response) => {
      setAddTocart(true);

      if (response?.isError) {
        setCartError(true);
        setLoading(false);
        setIsCartDisable(false);
      } else {
        setLoading(false);
        setAddedVariant((variant as any)?.sku ?? product?.variants?.[0]?.sku); // Set the added variant SKU
        setIsCartDisable(false);
        // show(product, variant, quantity);
        onAddToCart?.();
        const dataLayerProductDetailPush = new DataLayerHelpers(pathname, locale);
        dataLayerProductDetailPush.pushProductAddToCartContent(
          response?.data?.lineItems?.at?.(-1),
          cookies?.locale_selection?.currency,
        );
      }
    });
    const variantCode =
      variant?.attributes?.variantCode?.replace(/[\s/]+/g, '_') ??
      product?.variants?.[0]?.attributes?.variantCode?.replace(/[\s/]+/g, '_');
    if (objectID)
      analytics?.handleAddedToCartObjectIDs(
        variantCode,
        userId,
        isSearchItem ? 'SRP: Add to Cart' : 'PDP: Add to Cart',
        searchQueryId,
      );
    const newEntry = `${variantCode}:${searchQueryId}`;
    const existing = JSON?.parse(sessionStorage?.getItem(OBJECT_ID_QUERY_ID) || '[]');
    if (!existing?.includes(newEntry)) {
      existing.push(newEntry);
      sessionStorage?.setItem(OBJECT_ID_QUERY_ID, JSON?.stringify(existing));
    }
    trackAddToCart();
  };

  const fetchData = async () => {
    const storedVariants = typeof window !== 'undefined' ? localStorage.getItem('RecentlyViewed') : '[]';
    let viewedVariants: any[] = storedVariants ? JSON.parse(storedVariants as string)?.filter(Boolean) : [];
    viewedVariants = viewedVariants?.filter((item) => item?.productId !== product?.productId);
    if (product?.productId && !variant?.attributes?.isGiftcard) {
      const updatedVariants = [product, ...viewedVariants].slice(0, 10);
      localStorage.setItem('RecentlyViewed', JSON.stringify(updatedVariants));
    }
    fetchRelatedProducts(product);
  };
  useEffect(() => {
    fetchData();
  }, [product, fetchRelatedProducts, variant]);

  useEffect(() => {
    if (objectID && !initialViewTracked?.current) {
      analytics?.handlePDPViewedProdut(objectID, userId);
      initialViewTracked.current = true; // Mark as tracked
    }
  }, [objectID]);

  useEffect(() => {
    setProductLogo(productLogo as string);
  }, [productLogo]);

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 px-20 md:pr-36'
    : 'pt-20 gap-[1rem] md:pt-24 pb-32 md:grid md:grid-cols-12 md:items-start lg:px-48 md:px-24 px-16';

  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-6 lg:col-span-8 3xl:col-span-7 ',
    '',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-6 lg:col-span-4 3xl:col-span-5 md:px-[1rem]',
  ]);

  const addtowishlist = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (objectID && searchQueryId) analytics?.handleClickAddWishlist(objectID, userId, searchQueryId);
    if (!isLoggedIn) {
      router.push('/login');
    }
  };

  const productToWishlistLineItem = useMemo<LineItem>(() => {
    return {
      lineItemId: product?.productId ?? '',
      productId: product?.productId,
      name: product?.name,
      count: 1,
      variant: variant,
      addedAt: new Date(),
      _url: product?._url,
      queryID: searchQueryId || '',
    };
  }, [product, variant]);

  const viewStandardFitOptions = (product?.popupData as CustomFitPopUpData)?.buttonBlocks?.filter(
    (standardButton: ButtonBlock) =>
      isDesktopSize
        ? standardButton?.layout === 'desktop'
        : ismobile
        ? standardButton?.layout === 'mobile'
        : standardButton?.layout === 'desktop',
  );

  useEffect(() => {
    const swatchColorSkus = product?.variants
      ?.filter((v: any) => v?.attributes?.colour === variant?.attributes?.colour)
      .map((v: any) => StringHelpers.removeWhiteSpace(v?.sku))
      .join(',');
    if (swatchColorSkus) {
      setTrustPilotSkuId(swatchColorSkus);
      localStorage.setItem('currentlySelectedVariants', swatchColorSkus);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      const dataLayerProductDetailPush = new DataLayerHelpers(pathname, locale);
      dataLayerProductDetailPush.pushProductDetailToDataLayer(
        product,
        variant ?? product?.variants?.[0],
        cookies?.locale_selection?.currency,
        Selectedvariant,
      );
    }
  }, [isProduct]);
  useEffect(() => {
    if (selectedCurrency && product?.customFitDetails?.[0]) {
      setCustomFitPrice(product?.customFitDetails?.[0].productJSON?.CF_Price?.[selectedCurrency as any]);
    } else {
      setCustomFitPrice(null);
    }
  }, [selectedCurrency, product]);

  useEffect(() => {
    if (customFitVariant) {
      setIsCartDisable(true);
    }
  }, []);

  useEffect(() => {
    const localeSelection = getCurrencyCookie('locale_selection');
    setSelectedCurrency(localeSelection?.currency || null);
  }, [locale]);

  useEffect(() => {
    if (product) {
      setProduct(true);
    }
  }, [product]);

  const hasTriggered = useRef(false);

  useEffect(() => {
    const shouldDisable =
      isCartDisable ||
      (!customFitVariant &&
        !isPersonalized &&
        (product?.customFitDetails === undefined || product?.customFitDetails === null) &&
        (!variant?.isOnStock || !finalPrice));

    setIsbuttonDisable(shouldDisable);
  }, [isCartDisable, customFitVariant, product?.customFitDetails, variant?.isOnStock, finalPrice]);

  useEffect(() => {
    if (isTimeout && selectedOption) {
      const timer = setTimeout(() => {
        setIsTimeout(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isTimeout, selectedOption]);
  const isPopupActive =
    popUpData?.popups?.some(
      (item) =>
        item?.isPopupActive === true &&
        item?.brandName?.toLowerCase() === product?.variants?.[0]?.attributes?.brand?.toLowerCase() &&
        product?.variants?.[0]?.attributes?.isCustomFit,
    ) ?? false;
  const isPDP = true;
  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchTrustPilotData(product, variant, undefined, isPDP);

      if (result) {
        setStarsAverage(result?.starsAverage);
        setNumberOfReviews(result?.numberOfReviews);
        setStarImage(() => result?.StarImageComponent);
      }
    };

    fetchData();
  }, [product, variant]);

  useEffect(() => {
    if (product && isPersonalized) {
      setQuantity(minOrderQuantity);
    }
  }, [isPersonalized]);

  return (
    <>
      {isTimeout && isPopupActive && (
        <div className="relative z-50 flex w-full items-center justify-center">
          <PopUp popupData={popUpData} selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
          <div className="fixed inset-0 -z-10 bg-black bg-opacity-30"></div>
        </div>
      )}
      <div className={isPopupActive && isTimeout ? 'pointer-events-none relative select-none  filter' : ''}>
        {breadCrumb?.length > 0 && !inModalVersion && (
          <Breadcrumb
            useSeparatorNode
            Separator={
              <div className="relative top-[2px] mb-2 text-26 font-bold text-black">
                <ChevronRight />
              </div>
            }
            className="mx-auto mb-[0.5rem] mt-20 flex w-full max-w-[104rem] flex-wrap lg:hidden"
            listClassName="flex flex-wrap gap-7"
          >
            {breadCrumb?.map((breadCrumb) => {
              // const isActive = pathname == breadCrumb.href;//
              return (
                <SmartLink
                  key={breadCrumb?.href}
                  href={breadCrumb?.href}
                  showLoader={false}
                  className={`text-14 font-normal ${'font-semibold text-black'}`}
                >
                  {breadCrumb?.name}
                </SmartLink>
              );
            })}
          </Breadcrumb>
        )}
        {product ? (
          <div className="mx-auto max-w-[104rem]">
            <div className={wrapperClassName}>
              {breadCrumb?.length > 0 && !inModalVersion && (
                <Breadcrumb
                  useSeparatorNode
                  Separator={
                    <div
                      className="relative top-[2px] mb-2 text-26  font-bold text-black
               "
                    >
                      <ChevronRight />
                    </div>
                  }
                  className="col-span-12 mb-[0.5rem] hidden w-fit lg:block "
                  listClassName="px-0 gap-7"
                  dynamicPadding={true}
                  style={{
                    paddingLeft: `calc((100% - 104rem) / 2 + 92px + 1rem)`,
                  }}
                >
                  {breadCrumb?.map((breadCrumb) => {
                    // const isActive = pathname == breadCrumb.href;
                    return (
                      <SmartLink
                        key={breadCrumb?.href}
                        showLoader={false}
                        href={breadCrumb?.href}
                        className={`'font-semibold text-14 font-semibold text-black `}
                      >
                        {breadCrumb?.name}
                      </SmartLink>
                    );
                  })}
                </Breadcrumb>
              )}
              <div className={galleryContainerClassName}>
                {product && (
                  <Gallery
                    productLogo={productLogo}
                    images={colorVariant?.images ?? variant?.images ?? product?.variants?.[0].images ?? []}
                    inModalVersion={inModalVersion}
                    product={product}
                    variant={variant}
                    hoveredImage={hoveredImage}
                    breadCrumb={breadCrumb}
                    viewStandardFitOptions={viewStandardFitOptions}
                    allBadges={allBadges}
                    isDesktopSize={isDesktopSize}
                    isMobile={isMobile}
                    selectedLocale={selectedLocale}
                    trustBoxCss={trustBoxCss}
                    cartDiscountBadge={cartDiscountBadge}
                    trustPilotSkuId={trustPilotSkuId}
                    StarImageComponent={StarImage}
                    starsAverage={starsAverage}
                    numberOfReviews={numberOfReviews}
                    handleScrollReviews={handleScrollReviews}
                    discountPercentage={discountPercentage}
                    mrrp={mrrp}
                    variantPrice={variantPrice}
                  />
                )}
              </div>

              <div className={informationContainerClassName}>
                {product ? (
                  <ProductInformation
                    pdpProduct={pdpProduct}
                    product={product}
                    variant={variant}
                    onChangeVariant={onChangeVariant}
                    inModalVersion={inModalVersion}
                    handleScrollReviews={handleScrollReviews}
                    setIsCartDisable={setIsCartDisable}
                    setHoveredImage={setHoveredImage}
                    setCustomFitPrice={setCustomFitPrice}
                    customFitPrice={customFitPrice}
                    setQuantity={setQuantity}
                    quantity={quantity}
                    setPayloadAttributes={setPayloadAttributes}
                    deliveryDate={deliveryDate}
                    setDeliveryDate={setDeliveryDate}
                    setSelectedSKUs={setSelectedSKUs}
                    payloadAttributes={payloadAttributes}
                    isCartDisable={isbuttonDisable}
                    selectedSKUs={selectedSKUs}
                    breadCrumb={breadCrumb}
                    handleColorVariant={handleColorVariant}
                    colorVariant={colorVariant}
                    viewStandardFitOptions={viewStandardFitOptions}
                    trustBoxCss={trustBoxCss}
                    allBadges={allBadges}
                    cartDiscountBadge={cartDiscountBadge}
                    isDesktopSize={isDesktopSize}
                    isMobile={isMobile}
                    selectedLocale={selectedLocale}
                    StarImageComponent={StarImage}
                    starsAverage={starsAverage}
                    numberOfReviews={numberOfReviews}
                    setDiscountedMRPrices={setDiscountedMRPrices}
                    discountedMRPrices={discountedMRPrices}
                    isPersonalized={isPersonalized}
                    personalisationBalls={personalisationBalls}
                    minOrderQuantity={minOrderQuantity}
                    onPersonalisationChange={setPersonalisationData}
                    personalisationData={personalisationData}
                  />
                ) : (
                  <Loading />
                )}

                {!(customFitVariant || product?.customFitDetails) &&
                  !isPersonalized &&
                  !(variant?.isOnStock ?? product?.variants?.[0].isOnStock) && (
                    <div className="pt-20">
                      <p className="font-medium text-[#EB0E0E]">
                        {formatProductMessage({ id: 'outOfStock', defaultMessage: 'Out of stock' })}
                      </p>
                    </div>
                  )}

                <div className="mb-10 flex w-[100%] max-w-none  flex-row-reverse gap-8 pt-[1rem]">
                  {/* <Dropdown
                            className="h-full rounded-sm"
                            value={quantity.toString()}
                            items={Array(10)
                                .fill(0)
                                .map((_, index) => {
                                    const value = `${index + 1}`;
                                    return {
                                        label: value,
                                        value,
                                    };
                                })}
                            onChange={handleQuantityChange}
                        /> */}
                  <button
                    onClick={addtowishlist}
                    className="box-border flex h-[50px] min-w-[50px] cursor-pointer items-center justify-center rounded-full border-2"
                  >
                    <WishlistButton lineItem={productToWishlistLineItem} className="h-[20px] w-[20px]" />
                  </button>

                  {addedVariant === (variant?.sku ?? product?.variants?.[0]?.sku) ? (
                    <div className="flex w-full flex-col gap-y-5 xl:flex-row xl:gap-x-6 xl:gap-y-0">
                      <Button
                        className="min-h-[42px] w-full rounded-sm px-12 py-4 font-body text-16 font-medium leading-[22px]"
                        variant="primary"
                        onClick={handleAddToCart}
                        loading={loading}
                        disabled={isCartDisable}
                      >
                        {formatProductMessage({ id: 'addAnotherToBasket', defaultMessage: 'Add another to basket' })}
                      </Button>
                      <Button
                        className="w-full rounded-sm text-16 font-medium"
                        variant="secondary"
                        onClick={() => router.push('/cart')}
                        // onClick={
                        //   () => (window.location.href = '/cart')
                        // }
                      >
                        {formatProductMessage({ id: 'goToCart', defaultMessage: 'Go to cart' })}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full rounded-sm text-16 font-medium"
                      variant="primary"
                      onClick={handleAddToCart}
                      loading={loading}
                      disabled={isCartDisable}
                    >
                      {formatProductMessage({ id: 'addToCart', defaultMessage: 'Add to cart' })}
                    </Button>
                  )}
                </div>
                {cartError && (
                  <p className="text-15 text-[#EB0E0E]">
                    {' '}
                    {formatProductMessage({ id: 'product.disable', defaultMessage: 'Something Went Wrong' })}
                  </p>
                )}
                {Boolean(isCartDisable) && (
                  <span className="text-sm font-normal text-[#EB0E0E]">
                    {formatProductMessage({ id: 'product.disable', defaultMessage: 'Please Select Your Options' })}
                  </span>
                )}

                {(finalPrice >= paypalThresholdMin && finalPrice <= paypalThresholdMax) ||
                finalPrice >= v12ThresholdMin ? (
                  paypal?.isEnabled ? (
                    <div className="mb-15 mt-15 flex w-full max-w-none items-center gap-x-10 bg-[#F5F4F3] p-9">
                      <div>
                        <img
                          src={`https://${paypal?.paymentImage.defaultHost}/i/${paypal?.paymentImage.endpoint}/${paypal?.paymentImage.name}`}
                          alt={paypal?.paymentImage?.name}
                          className="max-w-72 h-19"
                        />
                      </div>
                      <div className="text-[#023088]">
                        <p className="font-body text-12 font-bold leading-4 text-[#023088]">{`Pay in ${paypal?.splitInfo} interest free payments of £${paypalDivideAmount}`}</p>
                        <p className="font-body text-12 font-medium leading-4 text-[#023088]">
                          {paypal?.paymentInfo}{' '}
                          <span onClick={handlePaypalPromotion} className="cursor-pointer underline">
                            {paypal?.linkPaymentInfoText}
                          </span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-15 mt-15 w-full bg-[#F5F4F3] p-9">
                      <PayPalMessages price={finalPrice} currency={cookies?.locale_selection?.currency} />
                    </div>
                  )
                ) : null}
                {v12?.isEnabled && (
                  <>
                    {/* Case 1: Only splitInfoV1 (250 - 499.99) */}
                    {finalPrice >= v12ThresholdMin && finalPrice <= v12ThresholdMax && (
                      <div className="mb-15 mt-15 flex w-full max-w-none items-center gap-16 bg-[#F5F4F3] p-16">
                        <div className="flex items-center justify-center">
                          <img
                            src={`https://${v12?.paymentImage?.endpoint}.a.bigcontent.io/v1/static/${v12?.paymentImage?.name}`}
                            alt={v12?.paymentImage?.name}
                          />
                        </div>
                        <div className="text-[#023088]">
                          <p className="font-body text-12 font-bold leading-[130%] text-[#023088]">
                            {`Pay in ${v12?.splitInfoV1} interest free payments of £${v12DivideAmountV1}`}
                          </p>
                          <p className="font-body text-12 font-medium leading-[130%] text-[#023088]">
                            {v12?.paymentInfo}{' '}
                            <span onClick={handleV12Promotion} className="cursor-pointer underline">
                              {v12?.linkPaymentInfoText}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Case 2: Both splitInfoV1 + splitInfoV2 (500+) */}
                    {finalPrice >= v12HighThreshold && (
                      <div className="mb-15 mt-15 flex w-full max-w-none items-center gap-16 bg-[#F5F4F3] p-16">
                        <div className="flex items-center justify-center">
                          <img
                            src={`https://${v12?.paymentImage?.endpoint}.a.bigcontent.io/v1/static/${v12?.paymentImage?.name}`}
                            alt={v12?.paymentImage?.name}
                          />
                        </div>
                        <div className="flex flex-col gap-16">
                          <div>
                            <p className="font-body text-12 font-bold leading-[130%] text-[#023088]">
                              {`Pay in ${v12?.splitInfoV1} interest free payments of £${v12DivideAmountV1}`}
                            </p>
                            <p className="font-body text-12 font-medium leading-[130%] text-[#023088]">
                              {v12?.paymentInfo}{' '}
                              <span onClick={handleV12Promotion} className="cursor-pointer underline">
                                {v12?.linkPaymentInfoText}
                              </span>
                            </p>
                          </div>
                          <div>
                            <p className="font-body text-12 font-bold leading-[130%] text-[#023088]">
                              {`Pay in ${v12?.splitInfoV2} interest free payments of £${v12DivideAmountV2}`}
                            </p>
                            <p className="font-body text-12 font-medium leading-[130%] text-[#023088]">
                              {v12?.paymentInfo}{' '}
                              <span onClick={handleV12Promotion} className="cursor-pointer underline">
                                {v12?.linkPaymentInfoText}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {customFitVariant && (
                  <SmartLink showLoader={false} href={customBookingLink ?? ''}>
                    <button className="mt-10 flex h-32 w-full items-center justify-center rounded-md border bg-[#1A4075] align-middle font-body text-14 font-normal leading-[16.8px] text-white hover:no-underline">
                      {/* <img
                        alt="ag_stick_white"
                        src="https://www.americangolf.co.uk/on/demandware.static/-/Sites/en_GB/v1726467217150/americangolf/hockey_stick_white.svg"
                        title="ag_stick_white"
                        className="mr-6 h-18 w-18"
                      /> */}
                      {formatProductMessage({ id: 'customfit', defaultMessage: 'Book a free custom fitting session' })}
                    </button>
                  </SmartLink>
                )}
                {/* As of now commented the ShippingSection because it is not in the Prod Site  */}
                {/* {!inModalVersion && <ShippingSection />} */}

                {inModalVersion && (
                  <SmartLink
                    href={url ?? ''}
                    className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
                    onClick={() => setIsOpen?.(false)}
                  >
                    {formatProductMessage({ id: 'moreDetails', defaultMessage: 'More details' })}
                  </SmartLink>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Loading />
        )}
        {/* Need to FIX */}
        {/* {!inModalVersion && !isEmptyParagraph && ( */}
        {!inModalVersion && (
          <div className="mt-20 grid gap-y-34 bg-[#e9e9e9] pt-10 md:col-span-7 md:mb-50 md:pr-20 lg:col-span-12">
            <AdditionalInfo
              productspec={variant?.attributes?.productspec}
              description={product?.description ?? ''}
              productImage={product?.images[0]?.src}
              trustPilotSkuId={trustPilotSkuId}
              selectedVariant={variant?.sku}
              scrollToRefReview={scrollToRefReview}
              setReviewOpen={setReviewOpen}
              reviewOpen={reviewOpen}
            />
          </div>
        )}
        {/* As of now commentted : Need to fix */}
        {/* {cartDiscount && (
        <div
          ref={scrollToRef}
          className="mt-20 flex min-h-[240px] w-full flex-col items-center justify-center gap-y-20 bg-[#00d195] py-15"
        >
          <p className="mb-5 font-body text-22 font-medium capitalize leading-[22px]">
            {cartDiscount?.name?.['en-GB']}
          </p>
          {cartDiscount?.description?.['en-GB'] && (
            <p className="mb-15 font-body text-18 font-semibold capitalize leading-[20px]">
              {cartDiscount?.description?.['en-GB']}
            </p>
          )}
          <Link link={`${parentCategoryLink}?pmid=${cartDiscount?.key}`}>
            <button className="h-40 w-92 rounded-md bg-[#1e1e1e] px-9 text-14 font-bold text-white">
              {formatMessage({ id: 'view.all', defaultMessage: 'VIEW ALL' })}
            </button>
          </Link>
        </div>
      )} */}
        <Modal
          isOpen={paypalModal}
          onRequestClose={() => {
            setPaypayModal(false);
            paymentOverlayContentRef.current = null;
          }}
          style={{
            content: {
              height: '100vh', // Full viewport height
              maxHeight: '100vh', // Prevents overflow
            },
          }}
        >
          <div className="relative flex flex-col items-center md:h-screen">
            {/* Close Button */}
            <div className="absolute right-4 top-4">
              <button className="text-red-600" onClick={() => setPaypayModal(false)}>
                Close ✖
              </button>
            </div>

            {/* Centered Content */}
            <div className="mb-24 mt-[3rem] flex items-center justify-center text-center">
              <div>
                <p className="font-body text-24 font-normal leading-6">{paypal?.modalHeading}</p>
                <p className="font-body text-14 font-normal leading-6">{paypal?.modalSubHeading}</p>
              </div>
            </div>

            {/* PayPal Card */}
            <div className="mb-35 flex w-full max-w-sm flex-col rounded-md border bg-white pb-4 shadow-md">
              <div className="mb-4 flex h-[50px] w-full items-center justify-center rounded-t-md bg-blue-600 py-2 text-white">
                <div>
                  <img
                    src={`https://${paypal?.modalImageMain?.defaultHost}/i/${paypal?.modalImageMain?.endpoint}/${paypal?.modalImageMain?.name}`}
                    alt={paypal?.paymentImage?.name}
                  />
                </div>
              </div>
              <div className="flex h-[350px] flex-col justify-between px-4">
                <div className="p-4">
                  <h2 className="text-lg font-semibold">{paypal?.mainTextbelowImage}</h2>
                  <p className="mt-2 font-medium text-green-600">{paypal?.payInText}</p>
                  <p className="mt-2 text-sm text-gray-600">{paypal?.payInDescription}</p>
                  <p className="mt-4 text-sm text-gray-600">{paypal?.textBelowDescription}</p>
                </div>
                <div className="rounded-b-md bg-gray-100 p-2 text-xs text-gray-500">{paypal?.LastDescription}</div>
              </div>
            </div>
            <div className="font-body text-12 font-normal leading-4 text-[#023088]">@{paypal?.Lasttext}</div>
          </div>
        </Modal>
        <ResponsiveV12Modal v12={v12} v12Modal={v12Modal} setV12Modal={setV12Modal} />
      </div>
    </>
  );
};

export default ProductDetails;
